import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  FileCheck2,
  Hand,
  History,
  Languages,
  CalendarDays,
  PackageCheck,
  Plane,
  Search,
  ShieldCheck,
  BookOpen,
  Download,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import AccordionSection from '../components/UI/AccordionSection';
import ConfirmModal from '../components/UI/ConfirmModal';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import FormSelect from '../components/UI/FormSelect';
import Modal from '../components/UI/Modal';
import PillToggle from '../components/UI/PillToggle';
import { Pagination } from '../components/UI/Pagination';
import { StatusBadge } from '../components/UI/StatusBadge';
import { CountBadge } from '../components/UI/CountBadge';
import { DepartmentTradeChips } from '../components/UI/DepartmentTradeChips';
import { Block } from '../components/handbook/DocumentBlocks';
import { formatDate, formatDateTime, formatDateTimeCompact, getCurrentSingaporeTime } from '../utils/dateUtils';
import { GUIDE_LANGUAGES } from '../config/userGuideLanguages';
import { useAuth } from '../contexts/AuthContext';
import {
  useAcknowledgePassportReturn,
  useDownloadPassportReturnReceiptPdf,
  useHandoverPassportReturn,
  useLaunchPassportReturnCampaign,
  useMarkPassportReturnHeldReady,
  useMarkPassportReturnReady,
  usePassportReturnHRWitnesses,
  usePassportReturnRecords,
  usePassportReturnRosterPreview,
  usePassportReturnSummary,
  useReceivePassportReturnIntoCustody,
  useRecordPassportReturnException,
  useSeedPassportReturnRoster,
} from '../hooks/usePassportReturns';

// Client-side page size for the HR worklist — large enough for a handover
// batch, small enough that a 100+ worker campaign stays scannable on mobile.
const HR_WORKLIST_PAGE_SIZE = 25;
const WITHDRAWAL_REASONS = ['passport_renewal', 'personal_matter'];
const isWithdrawalRecord = (record) =>
  WITHDRAWAL_REASONS.includes(record?.already_with_reason);

const guideLanguage = (code) => GUIDE_LANGUAGES.find((item) => item.code === code);

const languageEnglishName = (translation) =>
  guideLanguage(translation?.language_code)?.label || translation?.language_name || '';

const languageNativeName = (translation) =>
  translation?.language_name
  || guideLanguage(translation?.language_code)?.nativeLabel
  || '';

/** English first so HR can identify the language; native script confirms the recorded copy. */
const languageAuditLabel = (translation) => {
  const english = languageEnglishName(translation);
  const native = languageNativeName(translation);
  if (!english) return native;
  if (!native || native === english) return english;
  return `${english} · ${native}`;
};

const PassportDocumentDetails = ({
  passport,
  theme = 'employee',
  caption,
  hint,
  emptyLabel = 'Not on file',
}) => {
  const isAdmin = theme === 'admin';
  const number = (passport?.number || '').trim();
  const expiry = passport?.expiry_date;
  const labelClasses = isAdmin ? 'text-red-700' : 'text-blue-700';
  const valueClasses = isAdmin ? 'text-red-950' : 'text-blue-950';
  const hintClasses = isAdmin ? 'text-red-800' : 'text-blue-800';
  const shell = isAdmin
    ? 'border-red-100 bg-red-50/70'
    : 'border-blue-100 bg-blue-50/70';

  return (
    <div className={`rounded-xl border p-3 sm:p-4 ${shell}`}>
      {caption && (
        <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${labelClasses}`}>
          {caption}
        </p>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <dt className={`text-xs ${labelClasses}`}>Passport number</dt>
          <dd className={`text-sm font-medium tabular-nums tracking-wide break-all ${number ? valueClasses : 'text-gray-500'}`}>
            {number || emptyLabel}
          </dd>
        </div>
        <div>
          <dt className={`text-xs ${labelClasses}`}>Expiry</dt>
          <dd className={`text-sm font-medium tabular-nums ${expiry ? valueClasses : 'text-gray-500'}`}>
            {expiry ? formatDate(expiry) : emptyLabel}
          </dd>
        </div>
      </dl>
      {hint && (
        <p className={`text-sm mt-3 ${hintClasses}`}>{hint}</p>
      )}
    </div>
  );
};

const STATUS_META = {
  held: { status: 'pending', label: 'Held by HR' },
  ready: { status: 'pending', label: 'Ready for handover' },
  handed_over: { status: 'in_progress', label: 'With worker · acknowledgement pending' },
  on_leave: { status: 'pending', label: 'Already with worker · on leave' },
  ack_due: { status: 'in_progress', label: 'Policy acknowledgement due' },
  acknowledged: { status: 'completed', label: 'Acknowledged' },
  exception: { status: 'completed', label: 'Witnessed exception' },
};

// Cohort tabs are work queues (not 1:1 with summary status cards).
// "Physical return" = still in the held → ready → handed_over handover loop.
const COHORT_TABS = [
  { id: 'held', label: 'Physical return', statuses: ['held', 'ready', 'handed_over'] },
  { id: 'on_leave', label: 'On leave', statuses: ['on_leave'] },
  { id: 'ack_due', label: 'Ack due', statuses: ['ack_due'] },
  { id: 'done', label: 'Complete', statuses: ['acknowledged', 'exception'] },
];

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
    <div className="flex items-start gap-2 sm:gap-3">
      <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
      </div>
      <div className="min-w-0">
        <p className="text-lg sm:text-2xl font-bold text-gray-900">{value || 0}</p>
        <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
      </div>
    </div>
  </div>
);

const tradeSearchText = (trade) => {
  if (!trade) return '';
  if (typeof trade === 'string') return trade;
  return `${trade.trade_code || ''} ${trade.name || ''}`;
};

const matchesInventoryQuery = (row, query) => {
  if (!query) return true;
  return `${row.name} ${row.staff_id} ${row.department || ''} ${tradeSearchText(row.trade)} ${row.leave_type || ''}`
    .toLowerCase()
    .includes(query);
};

const sgtTodayIso = () => {
  const date = getCurrentSingaporeTime();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const possessionRowMeta = (row) => {
  const type = row.leave_type || '';
  const today = sgtTodayIso();
  if (row.leave_start_date && row.leave_start_date > today) {
    return `${type} starts ${formatDate(row.leave_start_date)}`;
  }
  if (row.leave_end_date && row.leave_is_current === false) {
    return `${type} ended ${formatDate(row.leave_end_date)} · passport still with worker`;
  }
  if (row.leave_end_date) {
    return `${type} · ends ${formatDate(row.leave_end_date)}`;
  }
  return type;
};

const WorkerIdentity = ({
  name,
  staffId,
  department,
  trade,
  nameClassName = 'font-semibold text-gray-900 truncate',
}) => (
  <div className="min-w-0">
    <p className={nameClassName}>{name}</p>
    <p className="text-xs text-gray-500">{staffId}</p>
    <DepartmentTradeChips
      department={department}
      trade={trade}
      layout="inline"
      className="mt-1.5"
    />
  </div>
);

const InventoryPersonRow = ({ row, meta }) => (
  <div className="flex items-start justify-between gap-3 px-4 py-2.5 border-b border-gray-100 last:border-b-0">
    <WorkerIdentity
      name={row.name}
      staffId={row.staff_id}
      department={row.department}
      trade={row.trade}
      nameClassName="text-sm font-medium text-gray-900 truncate"
    />
    {meta ? (
      <p className="text-xs text-gray-500 flex-shrink-0 text-right max-w-[45%]">{meta}</p>
    ) : null}
  </div>
);

const InventoryAccordion = ({
  title,
  icon: Icon,
  iconBg,
  iconClass,
  rows,
  count,
  getMeta,
  expanded,
  onToggle,
  search,
  onSearch,
  searchPlaceholder,
}) => (
  <AccordionSection
    expanded={expanded}
    onToggle={onToggle}
    theme="admin"
    contentVariant="flush"
    icon={<Icon className={`w-4 h-4 ${iconClass}`} />}
    iconBg={iconBg}
    title={(
      <span className="inline-flex items-center gap-2 min-w-0">
        <span className="truncate">{title}</span>
        <CountBadge count={count ?? rows.length} theme="admin" className="flex-shrink-0" />
      </span>
    )}
  >
    <div className="border-t border-gray-100">
      {onSearch && (
        <div className="px-4 py-3">
          <DebouncedSearchInput
            value={search}
            onSearch={onSearch}
            placeholder={searchPlaceholder}
            theme="admin"
            className="max-w-md"
          />
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No matching workers</h3>
          <p className="text-sm text-gray-500">Clear the search to see the full list.</p>
        </div>
      ) : (
        <div className={rows.length > 8 ? 'max-h-72 overflow-y-auto' : ''}>
          {rows.map((row) => (
            <InventoryPersonRow
              key={row.staff_id}
              row={row}
              meta={getMeta ? getMeta(row) : row.leave_type}
            />
          ))}
        </div>
      )}
    </div>
  </AccordionSection>
);

const Timeline = ({ events = [] }) => (
  <div className="space-y-2">
    {events.map((event) => (
      <div key={event.id} className="flex items-start gap-2 text-xs text-gray-600">
        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
        <div>
          <span className="font-medium text-gray-800">{event.event_type_display}</span>
          {event.acted_by_name ? ` · ${event.acted_by_name}` : ''}
          <span className="block text-gray-400 tabular-nums">{formatDateTimeCompact(event.created_at)}</span>
        </div>
      </div>
    ))}
  </div>
);

const WorkerAcknowledgement = ({ record, campaign }) => {
  const translations = useMemo(
    () => campaign?.translations || [],
    [campaign?.translations],
  );
  const languageOptions = useMemo(
    () => translations.map((item) => ({ value: item.id, label: item.language_name })),
    [translations],
  );
  // Pre-select the worker's language based on their profile nationality
  // (resolved server-side in preferred_language_code). Worker can still
  // change it — this only saves the common case a tap.
  const defaultTranslation = useMemo(() => {
    const preferred = translations.find(
      (item) => item.language_code === record?.preferred_language_code,
    );
    return preferred
      || translations.find((item) => item.language_code === 'en')
      || translations[0];
  }, [translations, record?.preferred_language_code]);
  const [translationId, setTranslationId] = useState(defaultTranslation?.id || '');
  const [receiptConfirmed, setReceiptConfirmed] = useState(false);
  const [policyAcknowledged, setPolicyAcknowledged] = useState(false);
  const mutation = useAcknowledgePassportReturn();
  const translation = translations.find((item) => item.id === translationId);
  const isLeaveCohort = record.status === 'ack_due';
  const isWithdrawal = isWithdrawalRecord(record);
  const isPreselectedNonEnglish = Boolean(
    defaultTranslation
    && defaultTranslation.id === translationId
    && defaultTranslation.language_code !== 'en',
  );

  if (campaign?.status !== 'active') return null;
  if (record.status !== 'handed_over' && record.status !== 'ack_due') return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-blue-100 bg-blue-50">
        <div className="flex items-start gap-3">
          <FileCheck2 className="w-6 h-6 text-blue-700 flex-shrink-0" />
          <div>
            <h2 className="text-card-header text-blue-950">
              {isLeaveCohort ? 'Acknowledge passport possession and policy' : 'Confirm your passport receipt'}
            </h2>
            <p className="text-sm text-blue-800 mt-1">
              {isWithdrawal
                ? 'Your passport is already with you after a withdrawal. Confirm you still hold the original and acknowledge the policy. It will not return to HR safekeeping.'
                : isLeaveCohort
                  ? 'Your passport stayed with you while on leave. Confirm you still hold the original and acknowledge the policy. It will not return to HR safekeeping.'
                  : 'Confirm only after HR has physically handed your original passport to you.'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5 space-y-5">
        <div className="form-group">
          <label className="form-label">
            <Languages className="w-4 h-4 inline mr-1.5" />
            Policy language
          </label>
          <FormSelect
            options={languageOptions}
            value={translationId}
            onChange={setTranslationId}
            placeholder="Select language…"
            theme="employee"
            required
          />
          {isPreselectedNonEnglish && (
            <p className="form-help">
              Pre-selected based on your profile. Change it above if you prefer another language.
            </p>
          )}
        </div>

        {translation && (
          <section className="rounded-xl border border-gray-200 p-4 space-y-4">
            <div>
              <h3 className="text-card-header">{translation.title}</h3>
              <p className="text-xs text-gray-500 mt-1">
                Version {campaign.terms_version} · Reviewed {formatDate(translation.reviewed_at)}
              </p>
            </div>
            {translation.blocks.map((block, index) => (
              <Block key={`${block.t}-${index}`} block={block} />
            ))}
          </section>
        )}

        <PassportDocumentDetails
          passport={record.passport_on_file}
          theme="employee"
          caption="Passport on file"
          hint="Check this matches the book you are holding before you confirm."
        />

        <div className="space-y-3">
          <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={receiptConfirmed}
              onChange={(event) => setReceiptConfirmed(event.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-700 text-blue-700"
            />
            <span className="text-sm text-gray-800">
              {isLeaveCohort
                ? 'I confirm that my original passport is already with me and will remain with me.'
                : 'I confirm that I have received my original passport from HR and it is now with me.'}
            </span>
          </label>
          <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={policyAcknowledged}
              onChange={(event) => setPolicyAcknowledged(event.target.checked)}
              className="mt-0.5 w-4 h-4 accent-blue-700 text-blue-700"
            />
            <span className="text-sm text-gray-800">
              I have read this information in my selected language. I understand what I must do and will follow these responsibilities.
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={() => mutation.mutate({ id: record.id, translationId })}
          disabled={
            !translationId
            || !receiptConfirmed
            || !policyAcknowledged
            || mutation.isPending
          }
          className="btn btn-primary w-full disabled:opacity-50"
        >
          <CheckCircle2 className="w-4 h-4" />
          {mutation.isPending
            ? 'Recording…'
            : (isLeaveCohort ? 'Confirm possession and acknowledge' : 'Confirm receipt and acknowledge')}
        </button>
      </div>
    </div>
  );
};

const AcknowledgementReceipt = ({ record, campaign, theme = 'employee' }) => {
  const isAdmin = theme === 'admin';
  const [policyExpanded, setPolicyExpanded] = useState(isAdmin);
  const downloadPdf = useDownloadPassportReturnReceiptPdf();
  const acknowledgementEvent = [...(record.events || [])]
    .reverse()
    .find((event) => event.event_type === 'acknowledged');
  const acknowledgedTranslation = record.acknowledged_translation_snapshot
    || (campaign?.translations || []).find(
      (item) => item.content_hash === record.acknowledged_content_hash,
    );
  const englishTranslation = (campaign?.translations || []).find(
    (item) => item.language_code === 'en',
  );
  const [readingLanguage, setReadingLanguage] = useState(
    acknowledgedTranslation?.language_code || 'en',
  );
  const canViewEnglishReference = Boolean(
    acknowledgedTranslation
    && acknowledgedTranslation.language_code !== 'en'
    && englishTranslation,
  );
  const displayedTranslation = (
    readingLanguage === 'en' && englishTranslation
      ? englishTranslation
      : acknowledgedTranslation
  );

  if (record.status !== 'acknowledged') return null;

  const wasAlreadyWithWorker = acknowledgementEvent?.details?.prior_status === 'ack_due';
  const headerClasses = isAdmin
    ? 'border-red-100 bg-red-50'
    : 'border-blue-100 bg-blue-50';
  const headingClasses = isAdmin ? 'text-red-950' : 'text-blue-950';
  const supportingClasses = isAdmin ? 'text-red-800' : 'text-blue-800';
  const labelClasses = isAdmin ? 'text-red-700' : 'text-blue-700';

  const recordedPassport = record.acknowledged_passport || record.passport_on_file;
  const passportNumber = (recordedPassport?.number || '').trim();
  const downloadButton = (
    <button
      type="button"
      onClick={() => downloadPdf.mutate({
        id: record.id,
        filename: `Passport_Return_Acknowledgement_${record.worker_staff_id}.pdf`,
      })}
      disabled={downloadPdf.isPending}
      aria-label="Download PDF"
      title="Download PDF"
      className={`${isAdmin ? 'btn btn-admin-outline' : 'btn btn-outline'} flex-shrink-0`}
    >
      <Download className="w-4 h-4" />
      <span className="hidden sm:inline">
        {downloadPdf.isPending ? 'Preparing PDF…' : 'Download PDF'}
      </span>
    </button>
  );

  return (
    <div className={`bg-white rounded-2xl shadow-sm border ${isAdmin ? 'border-red-100' : 'border-blue-100'}`}>
      <div className={`p-4 sm:p-5 border-b ${headerClasses}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isAdmin ? 'bg-red-100' : 'bg-blue-100'}`}>
              <FileCheck2 className={`w-5 h-5 ${isAdmin ? 'text-red-700' : 'text-blue-800'}`} />
            </div>
            <div className="min-w-0">
              <h2 className={`text-card-header truncate ${headingClasses}`}>
                {isAdmin ? 'Recorded acknowledgement' : 'Your acknowledgement receipt'}
              </h2>
              <p className={`text-sm mt-1 line-clamp-2 ${supportingClasses}`}>
                {isAdmin
                  ? 'Read-only copy of the worker’s recorded confirmations and policy snapshot.'
                  : 'This is the recorded copy of what you confirmed. You can review it at any time.'}
              </p>
            </div>
          </div>
          {downloadButton}
        </div>

        <dl className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 mt-4">
          <div>
            <dt className={`text-xs ${labelClasses}`}>Recorded</dt>
            <dd className={`text-sm font-medium tabular-nums ${headingClasses}`}>
              {formatDateTime(record.acknowledged_at)}
            </dd>
          </div>
          <div>
            <dt className={`text-xs ${labelClasses}`}>Language</dt>
            <dd className={`text-sm font-medium ${headingClasses}`}>
              {languageAuditLabel(acknowledgedTranslation) || record.acknowledged_language}
            </dd>
          </div>
          <div>
            <dt className={`text-xs ${labelClasses}`}>Policy version</dt>
            <dd className={`text-sm font-medium ${headingClasses}`}>
              {campaign?.terms_version || 'Recorded version'}
            </dd>
          </div>
          <div>
            <dt className={`text-xs ${labelClasses}`}>Passport number</dt>
            <dd className={`text-sm font-medium tabular-nums tracking-wide break-all ${passportNumber ? headingClasses : 'text-gray-500'}`}>
              {passportNumber || 'Not on file'}
            </dd>
          </div>
          <div>
            <dt className={`text-xs ${labelClasses}`}>Expiry</dt>
            <dd className={`text-sm font-medium tabular-nums ${recordedPassport?.expiry_date ? headingClasses : 'text-gray-500'}`}>
              {recordedPassport?.expiry_date ? formatDate(recordedPassport.expiry_date) : 'Not on file'}
            </dd>
          </div>
        </dl>
        {!record.acknowledged_passport && (
          <p className={`text-xs mt-3 ${supportingClasses}`}>
            Passport details are from the current profile. They were not frozen when this receipt was recorded.
          </p>
        )}
      </div>

      <div className="p-4 sm:p-5">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 divide-y divide-emerald-200">
          <div className="flex items-start gap-3 p-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-950">
              {wasAlreadyWithWorker
                ? 'I confirm that my original passport is already with me and will remain with me.'
                : 'I confirm that I have received my original passport from HR and it is now with me.'}
            </p>
          </div>
          <div className="flex items-start gap-3 p-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-950">
              I have read this information in my selected language. I understand what I must do and will follow these responsibilities.
            </p>
          </div>
        </div>
      </div>

      {displayedTranslation && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5">
          <AccordionSection
            expanded={policyExpanded}
            onToggle={() => setPolicyExpanded((value) => !value)}
            theme={theme}
            contentVariant="padded"
            overflowVisible
            icon={<BookOpen className={`w-4 h-4 ${isAdmin ? 'text-red-700' : 'text-blue-800'}`} />}
            iconBg={isAdmin ? 'bg-red-100' : 'bg-blue-100'}
            title="Review policy content"
            className="shadow-none"
            trailing={canViewEnglishReference ? (
              <PillToggle
                value={readingLanguage}
                onChange={setReadingLanguage}
                options={[
                  {
                    id: acknowledgedTranslation.language_code,
                    label: isAdmin
                      ? languageEnglishName(acknowledgedTranslation)
                      : languageNativeName(acknowledgedTranslation),
                    title: `${languageAuditLabel(acknowledgedTranslation)} — acknowledged version`,
                  },
                  {
                    id: 'en',
                    label: 'English',
                    title: 'English — reference translation',
                  },
                ]}
                variant="text"
                theme={theme}
                ariaLabel="Policy reading language"
              />
            ) : null}
          >
            <div className="space-y-4">
              {canViewEnglishReference && readingLanguage === 'en' && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  English is shown for reference. The recorded acknowledgement remains in{' '}
                  {languageEnglishName(acknowledgedTranslation)}.
                </div>
              )}
              <div>
                <h3 className="text-card-header">{displayedTranslation.title}</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Version {campaign?.terms_version} · {languageAuditLabel(displayedTranslation)}
                  {readingLanguage === acknowledgedTranslation?.language_code
                    ? ' · Acknowledged copy'
                    : ' · Reference translation'}
                </p>
              </div>
              {displayedTranslation.blocks.map((block, index) => (
                <Block key={`${block.t}-${index}`} block={block} />
              ))}
            </div>
          </AccordionSection>
        </div>
      )}
    </div>
  );
};

const WorkerView = ({ records, campaign }) => {
  const record = records[0];
  if (!record) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="text-center py-12">
          <ShieldCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nothing for you yet</h3>
          <p className="text-sm text-gray-500">HR has not started your passport return. Check back after they launch the campaign.</p>
        </div>
      </div>
    );
  }
  const meta = STATUS_META[record.status] || STATUS_META.held;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your passport</p>
            <h2 className="text-card-header mt-1">{record.worker_name} · {record.worker_staff_id}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {record.status === 'held' && 'HR has verified that your passport is held and will arrange its return.'}
              {record.status === 'ready' && 'Your original passport is ready. Please collect it from HR.'}
              {record.status === 'handed_over' && 'HR recorded the physical handover. Your acknowledgement is required.'}
              {record.status === 'on_leave' && (
                `Your passport is already with you while on ${record.leave_type_name || 'leave'}. Acknowledge the policy after you return to work.`
              )}
              {record.status === 'ack_due' && (
                campaign?.status !== 'active'
                  ? `Your passport is already with you${record.leave_type_name ? ` (${record.leave_type_name})` : ''}. You will acknowledge the policy when HR launches the campaign.`
                  : isWithdrawalRecord(record)
                    ? `Your passport is already with you (${record.leave_type_name || 'withdrawn'}). Confirm you still hold it and acknowledge the policy.`
                    : 'Welcome back. Confirm you still hold your passport and acknowledge the policy.'
              )}
              {record.status === 'acknowledged' && 'Your receipt and policy acknowledgement are complete.'}
              {record.status === 'exception' && 'HR recorded the completed handover through a witnessed exception.'}
            </p>
          </div>
          <div className="flex-shrink-0">
            <StatusBadge status={meta.status} label={meta.label} />
          </div>
        </div>
        {(record.leave_end_date || record.handed_over_at || record.acknowledged_at) && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
            {record.leave_end_date && (
              <div>
                <dt className="text-xs text-gray-500">{record.leave_type_name || 'Leave'} ends</dt>
                <dd className="text-sm font-medium text-gray-900">{formatDate(record.leave_end_date)}</dd>
              </div>
            )}
            {record.leave_type_name && !record.leave_end_date && (
              <div>
                <dt className="text-xs text-gray-500">Why it is with you</dt>
                <dd className="text-sm font-medium text-gray-900">{record.leave_type_name}</dd>
              </div>
            )}
            {record.handed_over_at && (
              <div>
                <dt className="text-xs text-gray-500">Physically handed over</dt>
                <dd className="text-sm font-medium text-gray-900 tabular-nums">{formatDateTime(record.handed_over_at)}</dd>
              </div>
            )}
            {record.acknowledged_at && (
              <div>
                <dt className="text-xs text-gray-500">Acknowledged</dt>
                <dd className="text-sm font-medium text-gray-900 tabular-nums">
                  {formatDateTime(record.acknowledged_at)} · {record.acknowledged_language}
                </dd>
              </div>
            )}
          </dl>
        )}
      </div>

      {record.status === 'on_leave' && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Plane className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-950">No action while on leave</p>
              <p className="text-sm text-amber-900 mt-1">
                Keep your passport with you. When leave ends, open this page again to acknowledge the policy.
              </p>
            </div>
          </div>
        </div>
      )}

      <WorkerAcknowledgement record={record} campaign={campaign} />
      <AcknowledgementReceipt record={record} campaign={campaign} />

      {record.events?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-blue-700" />
            <h3 className="text-sm font-semibold text-gray-900">Return history</h3>
          </div>
          <Timeline events={record.events} />
        </div>
      )}
    </div>
  );
};

const HRRecordCard = ({
  record,
  onReady,
  onHandover,
  onReceive,
  onViewAcknowledgement,
  onException,
  pending,
}) => {
  const [expanded, setExpanded] = useState(false);
  const meta = STATUS_META[record.status] || STATUS_META.held;
  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="w-full p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
      >
        <div className="flex items-start justify-between gap-3">
          <WorkerIdentity
            name={record.worker_name}
            staffId={record.worker_staff_id}
            department={record.worker_department}
            trade={record.worker_trade}
          />
          <StatusBadge status={meta.status} label={meta.label} />
        </div>
      </button>
      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <dt className="text-xs text-gray-500">Custody verified</dt>
              <dd className="text-sm text-gray-800 tabular-nums">{formatDateTime(record.custody_verified_at)} · {record.custody_verified_by_name}</dd>
            </div>
            {record.leave_end_date && (
              <div>
                <dt className="text-xs text-gray-500">{record.leave_type_name || 'Leave'} ends</dt>
                <dd className="text-sm text-gray-800">{formatDate(record.leave_end_date)}</dd>
              </div>
            )}
            {record.leave_type_name && !record.leave_end_date && (
              <div>
                <dt className="text-xs text-gray-500">Already with worker</dt>
                <dd className="text-sm text-gray-800">{record.leave_type_name}</dd>
              </div>
            )}
            {record.handed_over_at && (
              <div>
                <dt className="text-xs text-gray-500">Handover</dt>
                <dd className="text-sm text-gray-800 tabular-nums">{formatDateTime(record.handed_over_at)} · {record.handed_over_by_name}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs text-gray-500">Passport number</dt>
              <dd className={`text-sm tabular-nums tracking-wide break-all ${record.passport_on_file?.number ? 'text-gray-800' : 'text-gray-500'}`}>
                {record.passport_on_file?.number || 'Not on file'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Passport expiry</dt>
              <dd className={`text-sm tabular-nums ${record.passport_on_file?.expiry_date ? 'text-gray-800' : 'text-gray-500'}`}>
                {record.passport_on_file?.expiry_date
                  ? formatDate(record.passport_on_file.expiry_date)
                  : 'Not on file'}
              </dd>
            </div>
          </dl>
          {record.exception_reason && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <span className="font-semibold">Witnessed exception:</span> {record.exception_reason}
              <span className="block text-xs mt-1">
                Witness · {record.exception_witness_name}
              </span>
            </div>
          )}
          <Timeline events={record.events} />
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            {record.status === 'held' && (
              <button
                type="button"
                onClick={() => onReady(record)}
                disabled={pending}
                className="btn btn-admin-primary w-full sm:w-auto"
              >
                Mark ready
              </button>
            )}
            {record.status === 'ready' && (
              <button
                type="button"
                onClick={() => onHandover(record)}
                disabled={pending}
                className="btn btn-admin-primary w-full sm:w-auto"
              >
                <Hand className="w-4 h-4" />
                Record physical handover
              </button>
            )}
            {(record.status === 'on_leave' || record.status === 'ack_due') && (
              <button
                type="button"
                onClick={() => onReceive(record)}
                disabled={pending}
                className="btn btn-admin-outline w-full sm:w-auto"
              >
                <PackageCheck className="w-4 h-4" />
                Passport now in the safe
              </button>
            )}
            {record.status === 'acknowledged' && (
              <button
                type="button"
                onClick={() => onViewAcknowledgement(record)}
                className="btn btn-admin-outline w-full sm:w-auto"
              >
                <FileCheck2 className="w-4 h-4" />
                View acknowledgement receipt
              </button>
            )}
            {(record.status === 'handed_over' || record.status === 'ack_due') && (
              <button
                type="button"
                onClick={() => onException(record)}
                disabled={pending}
                className="btn btn-warning w-full sm:w-auto"
              >
                <AlertTriangle className="w-4 h-4" />
                Record witnessed exception
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const HRView = ({ records, summary, isLoading }) => {
  const campaign = summary?.campaign;
  const isDraft = !campaign || campaign.status === 'draft';
  const isActive = campaign?.status === 'active';
  const counts = summary?.counts || {};
  const [inventoryConfirmed, setInventoryConfirmed] = useState(false);
  const [termsReviewed, setTermsReviewed] = useState(false);
  const [cohortTab, setCohortTab] = useState('held');
  const [search, setSearch] = useState('');
  const [heldQuery, setHeldQuery] = useState('');
  const [withWorkerQuery, setWithWorkerQuery] = useState('');
  const [openInventory, setOpenInventory] = useState({
    withWorker: true,
    held: true,
  });
  const [page, setPage] = useState(1);
  const [confirmation, setConfirmation] = useState(null);
  const [acknowledgementRecord, setAcknowledgementRecord] = useState(null);
  const [exceptionRecord, setExceptionRecord] = useState(null);
  const [witnessId, setWitnessId] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');

  const { data: preview, isLoading: previewLoading } = usePassportReturnRosterPreview({
    enabled: isDraft,
  });
  const { data: witnesses = [] } = usePassportReturnHRWitnesses({
    enabled: !!exceptionRecord,
  });
  const seedMutation = useSeedPassportReturnRoster();
  const launchMutation = useLaunchPassportReturnCampaign();
  const readyMutation = useMarkPassportReturnReady();
  const readyHeldMutation = useMarkPassportReturnHeldReady();
  const handoverMutation = useHandoverPassportReturn();
  const receiveMutation = useReceivePassportReturnIntoCustody();
  const exceptionMutation = useRecordPassportReturnException();
  const pending = readyMutation.isPending || readyHeldMutation.isPending || handoverMutation.isPending || receiveMutation.isPending || exceptionMutation.isPending;
  const heldCount = counts.held || 0;

  const activeTab = COHORT_TABS.find((tab) => tab.id === cohortTab) || COHORT_TABS[0];
  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    return records.filter((record) => {
      if (!activeTab.statuses.includes(record.status)) return false;
      if (!query) return true;
      return `${record.worker_name} ${record.worker_staff_id} ${record.worker_department || ''} ${tradeSearchText(record.worker_trade)} ${record.leave_type_name || ''}`
        .toLowerCase()
        .includes(query);
    });
  }, [records, search, activeTab]);

  // New cohort / search → start at page 1 so HR doesn't land mid-queue.
  useEffect(() => {
    setPage(1);
  }, [cohortTab, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / HR_WORKLIST_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRecords = useMemo(
    () => filteredRecords.slice(
      (currentPage - 1) * HR_WORKLIST_PAGE_SIZE,
      currentPage * HR_WORKLIST_PAGE_SIZE,
    ),
    [filteredRecords, currentPage],
  );

  // Prefer summary aggregates (DB-complete) over client-side list length so tab
  // badges stay correct even if the records payload is ever truncated again.
  const tabCount = (statuses) => {
    const fromSummary = statuses.reduce(
      (total, status) => total + (counts[status] || 0),
      0,
    );
    if (Object.keys(counts).length > 0) return fromSummary;
    return records.filter((record) => statuses.includes(record.status)).length;
  };

  const closeException = () => {
    setExceptionRecord(null);
    setWitnessId('');
    setExceptionReason('');
  };

  const submitException = () => {
    exceptionMutation.mutate(
      {
        id: exceptionRecord.id,
        witnessStaffId: witnessId,
        reason: exceptionReason.trim(),
      },
      { onSuccess: closeException },
    );
  };

  if (isLoading) return <LoadingSpinner />;

  const previewCounts = preview?.counts || summary?.roster_preview?.counts || {};
  const isPreSeed = isDraft && records.length === 0;
  const inventoryHeld = previewCounts.held ?? 0;
  const inventoryOnLeave = previewCounts.on_leave ?? 0;
  const inventoryWithdrawn = previewCounts.withdrawn ?? 0;
  const inventoryTotal = previewCounts.total ?? (inventoryHeld + inventoryOnLeave + inventoryWithdrawn);
  const resolvedCount = (counts.acknowledged || 0) + (counts.exception || 0);
  const leavePreviewRows = preview?.on_leave || summary?.roster_preview?.on_leave || [];
  const withdrawnPreviewRows = preview?.withdrawn || summary?.roster_preview?.withdrawn || [];
  const withWorkerRows = [...leavePreviewRows, ...withdrawnPreviewRows];
  const heldPreviewRows = preview?.held || summary?.roster_preview?.held || [];
  const excludedRows = preview?.excluded || summary?.roster_preview?.excluded || [];
  const heldQueryNormalized = heldQuery.trim().toLowerCase();
  const withWorkerQueryNormalized = withWorkerQuery.trim().toLowerCase();
  const filteredHeldRows = heldPreviewRows.filter((row) =>
    matchesInventoryQuery(row, heldQueryNormalized),
  );
  const filteredWithWorkerRows = withWorkerRows.filter((row) =>
    matchesInventoryQuery(row, withWorkerQueryNormalized),
  );
  const toggleInventory = (key) => {
    setOpenInventory((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <div className="space-y-5">
      {isPreSeed ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Who has the passport today
          </p>
          {previewLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <StatCard
                icon={ShieldCheck}
                label="In the HR safe"
                value={inventoryHeld}
                tone="bg-slate-100 text-slate-700"
              />
              <StatCard
                icon={BookOpen}
                label="Already with the worker"
                value={inventoryOnLeave + inventoryWithdrawn}
                tone="bg-amber-100 text-amber-700"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <StatCard icon={ShieldCheck} label="Held by HR" value={counts.held} tone="bg-red-100 text-red-700" />
          <StatCard icon={PackageCheck} label="Ready" value={counts.ready} tone="bg-amber-100 text-amber-700" />
          <StatCard icon={Hand} label="Handed over" value={counts.handed_over} tone="bg-blue-100 text-blue-700" />
          <StatCard icon={Plane} label="On leave" value={counts.on_leave} tone="bg-violet-100 text-violet-700" />
          <StatCard icon={CalendarDays} label="Ack due" value={counts.ack_due} tone="bg-orange-100 text-orange-700" />
          <StatCard icon={CheckCircle2} label="Complete" value={resolvedCount} tone="bg-emerald-100 text-emerald-700" />
        </div>
      )}

      {isDraft && (
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-5 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isPreSeed ? 'Check the cabinet against these lists' : 'Launch the campaign'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isPreSeed
                ? `Open both lists. ${inventoryHeld} passports should still be in the safe. The other ${inventoryOnLeave + inventoryWithdrawn} workers already have theirs — no physical return.`
                : 'Roster is loaded. Confirm translations were reviewed, then launch. Workers are not notified.'}
            </p>
          </div>

          {isPreSeed && !previewLoading && (
            <div className="space-y-3">
              <InventoryAccordion
                title="Already with the worker"
                icon={BookOpen}
                iconBg="bg-amber-100"
                iconClass="text-amber-700"
                rows={filteredWithWorkerRows}
                count={withWorkerRows.length}
                getMeta={possessionRowMeta}
                expanded={openInventory.withWorker}
                onToggle={() => toggleInventory('withWorker')}
                search={withWorkerQuery}
                onSearch={setWithWorkerQuery}
                searchPlaceholder="Search name, staff ID, department or trade…"
              />
              <InventoryAccordion
                title="In the HR safe · physical return"
                icon={ShieldCheck}
                iconBg="bg-slate-100"
                iconClass="text-slate-700"
                rows={filteredHeldRows}
                count={heldPreviewRows.length}
                expanded={openInventory.held}
                onToggle={() => toggleInventory('held')}
                search={heldQuery}
                onSearch={setHeldQuery}
                searchPlaceholder="Search name, staff ID, department or trade…"
              />
              {excludedRows.length > 0 && (
                <p className="text-sm text-gray-500 px-1">
                  {excludedRows.map((row) => `${row.name} (${row.staff_id})`).join(', ')}
                  {' '}not in this campaign — Malaysian WP. Passport was never held. No acknowledgement.
                </p>
              )}
            </div>
          )}

          {isPreSeed && (
            <div className="space-y-3 border-t border-gray-100 pt-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inventoryConfirmed}
                  onChange={(event) => setInventoryConfirmed(event.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-red-600 text-red-600"
                />
                <span className="text-sm text-gray-800">
                  I checked the lists. The safe holds {inventoryHeld} passports.
                  {' '}{inventoryOnLeave + inventoryWithdrawn} workers already have theirs.
                </span>
              </label>
              <button
                type="button"
                onClick={() => seedMutation.mutate()}
                disabled={!inventoryConfirmed || seedMutation.isPending || !(inventoryTotal > 0)}
                className="btn btn-admin-primary w-full sm:w-auto disabled:opacity-50"
              >
                {seedMutation.isPending
                  ? 'Loading roster…'
                  : `Load ${inventoryTotal} workers onto campaign`}
              </button>
            </div>
          )}

          {!isPreSeed && (
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsReviewed}
                  onChange={(event) => setTermsReviewed(event.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-red-600 text-red-600"
                />
                <span className="text-sm text-gray-800">
                  I confirm that management/legal has reviewed the English policy and every translated version before release.
                </span>
              </label>
              <button
                type="button"
                onClick={() => launchMutation.mutate()}
                disabled={!termsReviewed || launchMutation.isPending}
                className="btn btn-admin-primary w-full sm:w-auto disabled:opacity-50"
              >
                {launchMutation.isPending ? 'Launching…' : `Launch for ${records.length} worker${records.length === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
        </div>
      )}

      {campaign?.status === 'completed' && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-700 flex-shrink-0" />
            <div>
              <h2 className="font-semibold text-emerald-950">Campaign completed</h2>
              <p className="text-sm text-emerald-800 mt-1">
                Every registered passport left HR custody and every handover was resolved. This is a read-only archive.
              </p>
            </div>
          </div>
        </div>
      )}

      {(isActive || records.length > 0) && (
        <div className="space-y-3">
          {/* Navigation tabs: single-row horizontal scroll on mobile (DS Tab Navigation) */}
          <div
            role="tablist"
            aria-label="Passport return cohorts"
            className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth"
          >
            {COHORT_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={cohortTab === tab.id}
                onClick={() => setCohortTab(tab.id)}
                className={`tab-btn-admin ${cohortTab === tab.id ? 'active' : ''}`}
              >
                {tab.label}
                <CountBadge
                  count={tabCount(tab.statuses)}
                  theme="admin"
                  active={cohortTab === tab.id}
                  className="ml-2"
                />
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <DebouncedSearchInput
              value={search}
              onSearch={setSearch}
              placeholder="Search worker name, staff ID, department or trade…"
              theme="admin"
              className="max-w-md"
            />
            {isActive && heldCount > 0 && (
              <button
                type="button"
                onClick={() => setConfirmation({ type: 'ready-all' })}
                disabled={pending}
                className="btn btn-admin-primary w-full sm:w-auto"
              >
                Mark {heldCount} ready
              </button>
            )}
          </div>
          {filteredRecords.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No workers in this cohort</h3>
                <p className="text-sm text-gray-500">Switch tabs or clear the search.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {pageRecords.map((record) => (
                  <HRRecordCard
                    key={record.id}
                    record={record}
                    pending={pending}
                    onReady={(item) => setConfirmation({ type: 'ready', record: item })}
                    onHandover={(item) => setConfirmation({ type: 'handover', record: item })}
                    onReceive={(item) => setConfirmation({ type: 'receive', record: item })}
                    onViewAcknowledgement={setAcknowledgementRecord}
                    onException={setExceptionRecord}
                  />
                ))}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
                totalCount={filteredRecords.length}
                pageSize={HR_WORKLIST_PAGE_SIZE}
                theme="admin"
                noun="workers"
                variant="standalone"
              />
            </>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmation}
        onCancel={() => setConfirmation(null)}
        onConfirm={() => {
          if (confirmation.type === 'ready-all') {
            readyHeldMutation.mutate(undefined, { onSuccess: () => setConfirmation(null) });
            return;
          }
          const mutation = {
            ready: readyMutation,
            handover: handoverMutation,
            receive: receiveMutation,
          }[confirmation.type];
          mutation.mutate(confirmation.record.id, { onSuccess: () => setConfirmation(null) });
        }}
        title={
          confirmation?.type === 'ready'
            ? 'Mark passport ready?'
            : confirmation?.type === 'ready-all'
              ? `Mark ${heldCount} passports ready?`
              : confirmation?.type === 'receive'
                ? 'Passport is in the HR safe?'
                : 'Record physical handover?'
        }
        confirmLabel={
          confirmation?.type === 'ready'
            ? 'Mark ready'
            : confirmation?.type === 'ready-all'
              ? `Mark ${heldCount} ready`
              : confirmation?.type === 'receive'
                ? 'Move to held'
                : 'Confirm handover'
        }
        variant="primary"
        theme="admin"
        isLoading={readyMutation.isPending || readyHeldMutation.isPending || handoverMutation.isPending || receiveMutation.isPending}
      >
        <div className="space-y-3 text-sm text-gray-700">
          {confirmation?.record && (
            <WorkerIdentity
              name={confirmation.record.worker_name}
              staffId={confirmation.record.worker_staff_id}
              department={confirmation.record.worker_department}
              trade={confirmation.record.worker_trade}
              nameClassName="text-sm font-semibold text-gray-900 truncate"
            />
          )}
          {confirmation?.type === 'handover' && (
            <>
              <PassportDocumentDetails
                passport={confirmation.record.passport_on_file}
                theme="admin"
                caption="Passport on file"
                hint="Check this against the book in your hand."
              />
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-900">
                Confirm only after the original passport has been physically handed to the worker. Acknowledgement is not a condition of return.
              </div>
            </>
          )}
          {confirmation?.type === 'receive' && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-slate-800">
              Record this only after the original is physically in the cabinet. The worker will not be notified. Acknowledgement is due only after you hand it back.
            </div>
          )}
          {confirmation?.type === 'ready-all' && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-900">
              This is only for passports still held by HR. Each of those {heldCount} workers will be notified to collect. On-leave and acknowledgement-due records stay where they are.
            </div>
          )}
        </div>
      </ConfirmModal>

      <Modal
        isOpen={!!acknowledgementRecord}
        onClose={() => setAcknowledgementRecord(null)}
        title={
          acknowledgementRecord
            ? `Acknowledgement receipt · ${acknowledgementRecord.worker_name} · ${acknowledgementRecord.worker_staff_id}`
            : 'Acknowledgement receipt'
        }
        size="xl"
        theme="admin"
      >
        {acknowledgementRecord && (
          <AcknowledgementReceipt
            record={acknowledgementRecord}
            campaign={campaign}
            theme="admin"
          />
        )}
      </Modal>

      <Modal
        isOpen={!!exceptionRecord}
        onClose={closeException}
        title="Record witnessed exception"
        size="md"
        theme="admin"
        footer={(
          <>
            <button type="button" onClick={closeException} className="btn btn-admin-outline">Cancel</button>
            <button
              type="button"
              onClick={submitException}
              disabled={!witnessId || !exceptionReason.trim() || exceptionMutation.isPending}
              className="btn btn-warning disabled:opacity-50"
            >
              {exceptionMutation.isPending ? 'Recording…' : 'Record exception'}
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          {exceptionRecord && (
            <WorkerIdentity
              name={exceptionRecord.worker_name}
              staffId={exceptionRecord.worker_staff_id}
              department={exceptionRecord.worker_department}
              trade={exceptionRecord.worker_trade}
              nameClassName="text-sm font-semibold text-gray-900 truncate"
            />
          )}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Use only after physical handover, after return from leave, or when the passport was already withdrawn, if the worker cannot or refuses to acknowledge in the portal. The passport must not be taken back.
          </div>
          <div>
            <label className="form-label">Independent HR witness</label>
            <select
              value={witnessId}
              onChange={(event) => setWitnessId(event.target.value)}
              className="select-admin"
            >
              <option value="">Select witness…</option>
              {witnesses.map((witness) => (
                <option key={witness.staff_id} value={witness.staff_id}>
                  {witness.name} · {witness.staff_id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="exception-reason">Detailed reason</label>
            <textarea
              id="exception-reason"
              value={exceptionReason}
              onChange={(event) => setExceptionReason(event.target.value)}
              rows={4}
              className="textarea-admin"
              placeholder="Describe the handover, why self-acknowledgement was not obtained, and what the witness observed…"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const SelfServicePassportRequests = () => {
  const { user } = useAuth();
  const { data: summary, isLoading: summaryLoading } = usePassportReturnSummary();
  const canManage = !!summary?.can_manage;
  const { data: records = [], isLoading: recordsLoading } = usePassportReturnRecords({
    enabled: !!user,
  });

  return (
    <DashboardLayout>
      <div className="space-y-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              title="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-gray-900">Passport Return</h1>
                {summary?.pending_my_action > 0 && (
                  <CountBadge count={summary.pending_my_action} theme="warning" />
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {canManage
                  ? 'Check who already has their passport, load the roster, then launch.'
                  : 'Your passport stays with you after it is returned. It will not go back to HR.'}
              </p>
            </div>
          </div>
        </div>

        {(summaryLoading || recordsLoading) ? (
          <LoadingSpinner />
        ) : canManage ? (
          <HRView records={records} summary={summary} isLoading={recordsLoading} />
        ) : (
          <WorkerView records={records} campaign={summary?.campaign} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default SelfServicePassportRequests;
